import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class LoadingService {
    private readonly activeRequests = new Map<string, string>();
    private startedRequestsCount = 0;
    private completedRequestsCount = 0;
    private readonly minimumVisibleMs = 300;
    private loadingVisibleSince: number | null = null;
    private hideTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private readonly isLoadingSubject = new BehaviorSubject<boolean>(false);
    private readonly activeRequestsCountSubject = new BehaviorSubject<number>(0);
    private readonly completedRequestsCountSubject = new BehaviorSubject<number>(0);
    readonly isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();
    readonly activeRequestsCount$: Observable<number> = this.activeRequestsCountSubject.asObservable();
    readonly completedRequestsCount$: Observable<number> = this.completedRequestsCountSubject.asObservable();

    requestStarted(requestId: string, requestLabel: string): void {
        if (this.activeRequests.has(requestId)) {
            return;
        }
        if (this.hideTimeoutId) {
            clearTimeout(this.hideTimeoutId);
            this.hideTimeoutId = null;
        }
        this.activeRequests.set(requestId, requestLabel);
        this.startedRequestsCount += 1;
        this.syncState();
    }

    requestFinished(requestId: string): void {
        if (this.activeRequests.delete(requestId)) {
            this.completedRequestsCount += 1;
            this.syncState();
        }
    }

    // Kept for compatibility with any direct callers.
    show(): void {
        this.requestStarted(`manual-${Date.now()}-${Math.random().toString(36).slice(2)}`, 'manual');
    }

    // Kept for compatibility with any direct callers.
    hide(): void {
        const firstRequestId = this.activeRequests.keys().next().value;
        if (firstRequestId) {
            this.requestFinished(firstRequestId);
        } else {
            this.syncState();
        }
    }

    getActiveRequestsCount(): number {
        return this.activeRequests.size;
    }

    getCompletedRequestsCount(): number {
        return this.completedRequestsCount;
    }

    getActiveRequests(): string[] {
        return Array.from(this.activeRequests.values());
    }

    reset(): void {
        this.activeRequests.clear();
        this.startedRequestsCount = 0;
        this.completedRequestsCount = 0;
        this.syncState();
        this.completedRequestsCountSubject.next(0);
    }

    private syncState(): void {
        const activeCount = this.activeRequests.size;
        this.activeRequestsCountSubject.next(activeCount);
        this.completedRequestsCountSubject.next(this.completedRequestsCount);
        if (activeCount > 0) {
            this.loadingVisibleSince = this.loadingVisibleSince ?? Date.now();
            this.isLoadingSubject.next(true);
            return;
        }

        const elapsedVisibleMs = this.loadingVisibleSince ? Date.now() - this.loadingVisibleSince : this.minimumVisibleMs;
        const remainingVisibleMs = Math.max(this.minimumVisibleMs - elapsedVisibleMs, 0);

        if (remainingVisibleMs === 0) {
            this.loadingVisibleSince = null;
            this.isLoadingSubject.next(false);
            return;
        }

        if (this.hideTimeoutId) {
            clearTimeout(this.hideTimeoutId);
        }
        this.hideTimeoutId = setTimeout(() => {
            this.loadingVisibleSince = null;
            this.hideTimeoutId = null;
            if (this.activeRequests.size === 0) {
                this.isLoadingSubject.next(false);
            }
        }, remainingVisibleMs);
    }
}
